import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SpeedDial,
  SpeedDialAction,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import RateReviewIcon from "@mui/icons-material/RateReview";
import OverallTheme from "./Styles/OverallTheme";
import FeedbackForm from "./Components/Feedback/Feedback";
import AnimatedText from "./Components/Animated Logo/AnimatedLogo";
import NavBar from "./Components/NavBar/NavBar";
import RefreshIcon from "@mui/icons-material/Refresh";
import Papa from "papaparse";
import DynamicFilterMenu from "./Components/FilterMenu";
import { WrangleMemberData } from "./Utils/Wrangle";
import { CleanedData } from "./Utils/VariablesCleaningSpecification";
import QuestionSelector from "./Components/QuestionSelector";
import DynamicBarChart from "./Components/DynamicBarChart";
import { CountSubGroups } from "./Utils/Calculations";
import * as shapefile from "shapefile";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { GeoJSON } from "react-leaflet"; // Ensure you import GeoJSON from react-leaflet
import * as topojson from "topojson-client"; // Importing TopoJSON client library
import DynamicLineGraph from "./Components/DynamicLineChart";
const LineChartForRounds = ({ allRoundsData, selectedQuestion }) => {
  const [chartData, setChartData] = useState([]);
  const [chartLabels, setChartLabels] = useState([]);

  useEffect(() => {
    if (!allRoundsData.length || !selectedQuestion) {
      console.warn("No rounds data or selected question.");
      return;
    }

    const processChartData = () => {
      console.log("Processing chart data for question:", selectedQuestion);

      const responsePercentageByRound = {};

      allRoundsData.forEach((roundData) => {
        const { round, data } = roundData;

        if (!data || !data.length) {
          console.warn(`No data available for round '${round}'`);
          return;
        }

        // Check if selectedQuestion exists in the data structure
        const relevantResponses = data
          .map((item) => item[selectedQuestion])
          .filter((value) => value !== undefined && value !== null && value !== "");

        if (!relevantResponses.length) {
          console.warn(
            `No valid responses found for question '${selectedQuestion}' in round '${round}'`
          );
          return;
        }

        const totalResponses = relevantResponses.length;
        const responseCounts = relevantResponses.reduce((acc, response) => {
          acc[response] = (acc[response] || 0) + 1;
          return acc;
        }, {});

        // Calculate percentages for each response
        Object.keys(responseCounts).forEach((response) => {
          const percentage = (responseCounts[response] / totalResponses) * 100;

          if (!responsePercentageByRound[response]) {
            responsePercentageByRound[response] = {};
          }

          responsePercentageByRound[response][round] = percentage;
        });
      });

      console.log("Response percentages by round:", responsePercentageByRound);

      // Prepare data for the chart
      const chartLabels = allRoundsData
        .map((roundData) => roundData.round)
        .sort((a, b) => new Date(a) - new Date(b)); // Sort rounds chronologically

      const datasets = Object.keys(responsePercentageByRound).map((response) => ({
        label: response,
        data: chartLabels.map((round) => responsePercentageByRound[response][round] || 0),
        fill: false,
        borderColor: `#${Math.floor(Math.random() * 16777215).toString(16)}`, // Random color
        tension: 0.4,
      }));

      setChartData(datasets);
      setChartLabels(chartLabels);
    };

    processChartData();
  }, [allRoundsData, selectedQuestion]);

  if (!chartData.length || !chartLabels.length) {
    return <p>No data available for the selected question.</p>;
  }

  return (
    <DynamicLineGraph
      data={chartData}
      labels={chartLabels}
      title={`Response Trends for Question`}
    />
  );
};


const DashboardComponent = () => {
  // !------------- PARAMETERS -------------!
  const initialParams = {
    dashboard: {
      version: 1.0,
      name: "Polling Dashboard",
      company: "TARA",
      internal: false,
    },
    association: {
      company: "REAL",
      name: "Polling",
    },
  };
  const [Params, setParams] = useState(initialParams);
  const [filteredAllRoundsData, setFilteredAllRoundsData] = useState([]);

  // !!!----------- STYLING --------------!!!

  const [customColors, setCustomColors] = useState({});

  useEffect(() => {
    const initialColorTheme = Params.association.colortheme;
    setCustomColors({
      main: initialColorTheme?.main || "#fff",
      secondary: initialColorTheme?.secondary || "#000",
      accent: initialColorTheme?.accent || "#ccc",
    });
  }, [Params]);
  const company = Params.association.company;
  // Define a helper to retrieve colors based on the company name
  const getThemeColors = (companyName) => {
    // Check if the mode is 'Custom' and colortheme is defined
    if (companyName === "Custom") {
      const customTheme = Params.association.colortheme;

      // Check if customTheme is defined and has the required properties
      if (
        customTheme &&
        customTheme.main &&
        customTheme.secondary &&
        customTheme.accent
      ) {
        return customTheme;
      } else {
        // Fallback to default colors if customTheme is undefined or incomplete
        return {
          main: "#fff", // Default main colorT
          secondary: "#000", // Default secondary color
          accent: "#ccc", // Default accent color
        };
      }
    } else if (OverallTheme.palette[companyName]) {
      // Ensure company name exists in the palette before accessing
      return {
        main: Object.values(OverallTheme.palette[companyName].primary)[0],
        secondary: Object.values(OverallTheme.palette[companyName].primary)[1],
        accent: Object.values(OverallTheme.palette[companyName].secondary)[0],
      };
    } else {
      // Fallback colors if companyName is unexpected or not in the palette
      return {
        main: "#fff",
        secondary: "#000",
        accent: "#ccc",
      };
    }
  };

  const colors = getThemeColors(company);
  // !!!------------- MODALS -------------!!!
  // !------------- INFO MODAL -------------!
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const handleInfoModalToggle = () => {
    setOpenInfoModal(!openInfoModal);
  };
  // !--------- FEEDBACK MODAL -------------!
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const openFeedbackModal = () => setIsFeedbackModalOpen(true);
  const closeFeedbackModal = () => setIsFeedbackModalOpen(false);
  const feedbackButtonStyle = {
    position: "fixed",
    right: "20px",
    top: "75px",
    color: "white",
    border: "none",
    borderRadius: "20px",
    backgroundColor: colors.main,
    display: "flex",
    alignItems: "center",
    padding: "10px 20px",
    fontSize: "16px",
    cursor: "pointer",
    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
    transition: "background-color 0.3s ease",
  };

  // !!!------- INITIALIZATION -----------!!!
  const [isLoading, setIsLoading] = useState(true);
  const [displayText] = useState(Params.dashboard.name);
  const [questions, setQuestions] = useState([]);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [shapeData, setShapeData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);

  const [associationState, setAssociationState] = useState(null); 
  const [statesList, setStatesList] = useState([]);

  useEffect(() => {
    let timeoutId;

    const handleMessage = (event) => {
      if (!event.data || !Array.isArray(event.data)) {
        console.warn("No data received in message or data is not an array.");
        return;
      }

      let orgStates = event.data[10]; 

      if (
        Array.isArray(orgStates) &&
        orgStates.every((state) => typeof state === "string")
      ) {
        setStatesList(orgStates);
        clearTimeout(timeoutId);
      } else {
        console.warn(
          "OrgStates at index 10 not found or not in expected format. Searching the entire data array."
        );
      }
    };

    // Set a timeout for the overall process
    timeoutId = setTimeout(() => {
      const queryParams = new URLSearchParams(window.location.search);
      const stateFromQuery = queryParams.get("state");
      const defaultData = stateFromQuery ? stateFromQuery.split(",") : [];
      setStatesList(defaultData);
    }, 300); 

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (statesList.length > 0) {
      setAssociationState(statesList); 
    }
  }, [statesList]);

  console.log(associationState)
  const matchAndAnalyzeData = (shapeData, data, selectedQuestionName) => {
    if (!shapeData || !shapeData.features) {
      console.warn("shapeData is not structured as expected:", shapeData);
      return []; // Return an empty array if shapeData is not structured as expected
    }
  
    console.log('Data going into shape processing:', data);
  
    return shapeData.features.map((shape) => {
      // Modify the shape's name to remove specific words and characters
      const cleanedName = shape.properties.NAME.replace(/\b(AREA|ASSOCIATION OF REALTORS|INC|REALTORS)\b/gi, '')
                                              .replace(/[^\w\s]/gi, '')
                                              .trim()
                                              .toUpperCase();
      console.log('Processed shape name:', cleanedName);
  
      // Filter data associated with the current cleaned shape's name
      const responses = data.filter((data) => data.ASSOC === cleanedName);
  
      // Count each response type
      const responseCounts = responses.reduce((acc, curr) => {
        const responseKey = curr[selectedQuestionName];
        acc[responseKey] = (acc[responseKey] || 0) + 1;
        return acc;
      }, {});
  
      // Find the maximum count value
      const maxCount = Math.max(...Object.values(responseCounts), 0);
  
      // Find all responses that occur the maximum number of times
      const mostCommonResponses = Object.entries(responseCounts)
        .filter(([key, count]) => count === maxCount && maxCount !== 0)
        .map(([key]) => key);
  
      // Handle cases where there is no most common response or multiple most common responses
      let mostCommonResponse = mostCommonResponses.length === 1 ? mostCommonResponses[0] 
                                : mostCommonResponses.length > 1 ? mostCommonResponses
                                : 'No Members Present'; // Default if no responses or all counts are zero
  
      return { ...shape, mostCommonResponse };
    });
  };
  
  const [rounds, setRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [allRoundsData, setAllRoundsData] = useState([]);

  const stateRounds = {
    NJ: ["April 2025", "June 2024", "November 2023"],
    MN: ["February 2024"],
    CA: ["July 2024", "April 2023", "January 2023"],
  };
  
  useEffect(() => {
    // Load rounds for the selected state
    if (stateRounds[associationState]) {
      const rounds = stateRounds[associationState];
      const sortedRounds = rounds.sort((a, b) => new Date(b) - new Date(a)); // Sort rounds by date (latest first)
      setRounds(sortedRounds);
      setSelectedRound(sortedRounds[0]); // Set the latest round as default
    }
  }, [associationState]);
  
  useEffect(() => {
    const loadTopoData = async () => {
      if (!associationState || !selectedRound) return; // Only load if state and round are selected
  
      try {
        const response = await fetch(
          `Data/Cleaned-Shapefiles/Association-Included/Topojson/${associationState}.topojson`
        );
        const topoData = await response.json();
        if (topoData && topoData.objects) {
          const geoData = topojson.feature(
            topoData,
            topoData.objects[Object.keys(topoData.objects)[0]]
          );
  
          const normalizedData = geoData.features
            .filter((feature) => {
              const fileName = feature.properties.file_name
                ? feature.properties.file_name.toLowerCase()
                : "";
              return fileName.includes("assoc"); // Only include "association" level features
            })
            .map((feature) => ({
              ...feature,
              properties: {
                ...feature.properties,
                NAME: feature.properties.NAME, // Normalize properties if needed
              },
            }));
  
          setShapeData({
            ...geoData,
            features: normalizedData,
          });
        }
      } catch (error) {
        console.error("Error fetching or filtering topojson:", error);
      }
    };
  
    const fetchData = async (url) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch data from ${url}`);
        }
        const text = await response.text();
        const result = Papa.parse(text, { header: true });
        return result.data; // Return the parsed data
      } catch (error) {
        console.error("Error fetching data:", error);
        return []; // Return an empty array on error
      }
    };
    
    const loadAllRoundsData = async () => {
      if (!stateRounds[associationState]) return;
    
      const roundDataPromises = stateRounds[associationState].map(async (round) => {
        const [questions, rawData] = await Promise.all([
          fetchData(`Data/POLLING/${associationState}/${round}/Question Order Form.csv`),
          fetchData(`Data/POLLING/${associationState}/${round}/001_data.csv`),
        ]);
    
        const processedData = WrangleMemberData(rawData, CleanedData());
    
        // 🧹 CLEAN QUESTIONS: filter out if QName or Q is blank
        const cleanedQuestions = questions.filter((q) => {
          return q 
            && q.QName 
            && q.QName.trim() !== "" 
            && q.Q !== undefined 
            && q.Q !== null 
            && q.Q.toString().trim() !== "";
        });
    
        return { round, questions: cleanedQuestions, data: processedData };
      });
    
      const allRoundsData = await Promise.all(roundDataPromises);
    
      setAllRoundsData(allRoundsData);
      setFilteredAllRoundsData(allRoundsData);
    };
    
    loadTopoData();
    loadAllRoundsData();
  
    setTimeout(() => setIsLoading(false), 5000);
  }, [associationState, selectedRound]);
  
  useEffect(() => {
    if (selectedRound && allRoundsData.length > 0) {
      const currentRoundData = allRoundsData.find((r) => r.round === selectedRound);
      if (currentRoundData) {
        setQuestions(currentRoundData.questions || []);
        setData(currentRoundData.data || []);
        setOriginalData(currentRoundData.data || []);

      }
    }
  }, [selectedRound, allRoundsData]);

  console.log('ALL ROUNDS', allRoundsData)
  
  // !!!------- COMPONENTS -----------!!!

  
  const MiniCard = ({ title, value }) => {
    return (
      <>
        <Card
          style={{
            flex: 1,
            minWidth: 0,
            maxWidth: 460,
            minHeight: 100,
            maxHeight: 100,
            display: "flex",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
          }}
        >
          <CardContent style={{ flexGrow: 1, display: "flex" }}>
            <Grid
              container
              spacing={1}
              style={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Grid
                item
                xs
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  gutterBottom
                  variant="overline"
                  align="center"
                  style={{ wordWrap: "break-word" }}
                >
                  {title}
                </Typography>
              </Grid>
              <Grid
                item
                style={{ height: "70%", display: "flex", alignItems: "center" }}
              >
                <Divider orientation="vertical" flexItem />
              </Grid>
              <Grid
                item
                xs
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  color="textPrimary"
                  align="center"
                  variant="h5"
                  style={{ wordWrap: "break-word" }}
                >
                  {value}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </>
    );
  };

  // !------------- FILTERING -------------!
  const [openSpeedDial, setOpenSpeedDial] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const toggleFilterMenu = () => {
    setIsFilterMenuOpen(!isFilterMenuOpen);
  };

  const resetFilters = () => {
    setSelectedFilters({});
    setData(originalData);
    setFilteredAllRoundsData(allRoundsData); // Clear filtered rounds too
  };
  
  const applyFilters = (selectedFilters) => {
    let filteredCurrentRoundData = originalData.filter((item) => {
      return Object.entries(selectedFilters).every(([filterKey, filterValues]) => {
        if (!filterValues || filterValues.length === 0) {
          return true;
        }
        return filterValues.includes(item[filterKey]);
      });
    });
    setData(filteredCurrentRoundData); // Update for the current round
    setIsFilterMenuOpen(false);
  
    // Also filter all rounds data
    const filteredRounds = allRoundsData.map((roundData) => {
      const filteredData = roundData.data.filter((item) => {
        return Object.entries(selectedFilters).every(([filterKey, filterValues]) => {
          if (!filterValues || filterValues.length === 0) {
            return true;
          }
          return filterValues.includes(item[filterKey]);
        });
      });
      return { ...roundData, data: filteredData };
    });
  
    setFilteredAllRoundsData(filteredRounds); // Set filtered rounds
  };
  
  // !------------- QUESTIONS -------------!
  const [selectedQuestionName, setSelectedQuestionName] = useState(null);
  const [selectedArray1, setSelectedArray1] = useState([
    "ROLE",
    "ROLE",
    "Something else",
  ]);
  const [selectedArray2, setSelectedArray2] = useState(["Placeholder", "QUESTION", "Unknown"]); // Temporary placeholder
  
  useEffect(() => {
    // After questions are loaded, set `selectedArray2` to the first row's `QName`
    if (questions.length > 0) {
      const firstQName = questions[5]?.QName; // Ensure there is a QName in the first row
      if (firstQName) {
        setSelectedArray2([firstQName, "QUESTION", "Unknown"]);
        setSelectedQuestionName(firstQName); // Also set as selected question name
      }
    }
  }, [questions]); // This will re-run only when `questions` changes
  
  const handleQuestionChange = (questionName) => {
    setSelectedQuestionName(questionName);
    setSelectedArray2([questionName, "QUESTION", "Unknown"]); // Update selectedArray2 only when a new question is selected
  };
  
  useEffect(() => {
    // Update selectedArray2 whenever selectedQuestionName changes
    if (selectedQuestionName) {
      setSelectedArray2([selectedQuestionName, "QUESTION", "Unknown"]);
    }
  }, [selectedQuestionName]);
  const handleSelectedArrayChange1 = (array) => {
    setSelectedArray1(array);
  };

  const handleSelectedArrayChange2 = (array) => {
    setSelectedArray2(array);
  };

  const DropdownSelect = ({ selectedArray, onSelectedArrayChange }) => {
    const [selectedOption, setSelectedOption] = useState(
      selectedArray[0] || "Gender"
    );

    const handleSelectChange = (event) => {
      const newSelectedOption = event.target.value;
      setSelectedOption(newSelectedOption);
      const selectedArray = getSelectedArray(newSelectedOption);
      onSelectedArrayChange(selectedArray);
    };

    const getSelectedArray = (selectedOption) => {
      switch (selectedOption) {
        case "GENDER":
          return ["GENDER", "GENDER", "UNKNOWN"];
        case "ROLE":
          return ["ROLE", "ROLE", "Other"];
        case "TENURE":
          return ["TENURE", "TENURE", "Other"];
        case "AGE":
          return ["AGE", "AGE", "Other"];
        default:
          return [];
      }
    };

    return (
      <div>
        <FormControl fullWidth variant="outlined">
          <InputLabel id="selectOption-label">Select an option</InputLabel>
          <Select
            labelId="selectOption-label"
            id="selectOption"
            value={selectedOption}
            onChange={handleSelectChange}
            label="Select an option"
          >
            <MenuItem value="GENDER">GENDER</MenuItem>
            <MenuItem value="ROLE">ROLE</MenuItem>
            <MenuItem value="TENURE">TENURE</MenuItem>
            <MenuItem value="AGE">AGE</MenuItem>
          </Select>
        </FormControl>
      </div>
    );
  };
  const dynamicChartGrouping = useMemo(() => {
    const preprocessData = data.map(item => {
      const raceMergeCategories = ["UNKNOWN", "UNCODED", "UNMATCHED"];
      const genderMergeCategories = ["UNKNOWN", "UNMATCHED", "UNKNOWN"];
  
      let newItem = {...item};
      if (raceMergeCategories.includes(newItem.RACE)) {
        newItem.RACE = "UNKNOWN";
      }
      if (newItem.GENDER && genderMergeCategories.includes(newItem.GENDER)) {
        newItem.GENDER = "UNKNOWN";
      }
      return newItem;
    });
  
    let processedData = CountSubGroups(preprocessData, [selectedArray1, selectedArray2])
      .map(({ value, ...rest }) => ({
        ...rest, Total: value
      }))
      .filter(group => {
        // Filter out any entry where any key has 'Other' as a value
        return group != null && !Object.values(group).includes('Other');
      });
  
    // Define the order arrays
    const ageOrder = ["(No answer)", "18 - 24", "25 - 29", "30 - 34", "35 - 39", "40 - 44", "45 - 49", "50 - 54", "55 - 59", "60 - 64", "Over 64"];
    const tenureOrder = ["Less than 2 years", "2-4 years", "5-9 years", "10-14 years", "15-19 years", "20+ years"];
  
    // Sorting logic that considers both TENURE and AGE, with a fallback
    processedData.sort((a, b) => {
      let primaryDiff = 0; // Used for the primary sorting difference (either TENURE or AGE)
      let secondaryDiff = 0; // Used for secondary sorting within the primary sort
  
      if ((selectedArray1.includes("TENURE") || selectedArray2.includes("TENURE")) && a.TENURE && b.TENURE) {
        primaryDiff = tenureOrder.indexOf(a.TENURE) - tenureOrder.indexOf(b.TENURE);
      } else if ((selectedArray1.includes("AGE") || selectedArray2.includes("AGE")) && a.AGE && b.AGE) {
        primaryDiff = ageOrder.indexOf(a.AGE) - ageOrder.indexOf(b.AGE);
      }
  
      // Secondary sorting by QUESTION if primary criteria are the same
      if (primaryDiff === 0 && a.QUESTION && b.QUESTION) {
        secondaryDiff = a.QUESTION.localeCompare(b.QUESTION);
      }
  
      return primaryDiff || secondaryDiff; // Apply primary sort first, then secondary sort
    });
  
    // If neither TENURE nor AGE are selected, sort by QUESTION as a default
    if (!selectedArray1.some(r => ["TENURE", "AGE"].includes(r)) && !selectedArray2.some(r => ["TENURE", "AGE"].includes(r))) {
      processedData.sort((a, b) => (a.QUESTION && b.QUESTION) ? a.QUESTION.localeCompare(b.QUESTION) : 0);
    }
  
    console.log('Sorted Data:', processedData);
    return processedData;
  }, [data, selectedArray1, selectedArray2]);
  
  
  
  // !------------- MAP CHART -------------!

  const [colorStart, colorEnd] = Object.values(
    OverallTheme.palette.REAL.primary
  ).slice(0, 2);
  // Convert a hex color to its RGB components
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16),
        ]
      : null;
  };

  // Function to interpolate between two colors
  const interpolateColor = (color1, color2, factor = 3) => {
    return color1.map((c1, i) => Math.round(c1 + factor * (color2[i] - c1)));
  };

  // Create a gradient array of colors
  const createGradient = (colorStart, colorEnd, categoriesCount) => {
    const startRgb = hexToRgb(colorStart);
    const endRgb = hexToRgb(colorEnd);
    const colors = [];

    for (let i = 0; i < categoriesCount; i++) {
      // Linear interpolation factor
      const factor = i / (categoriesCount - 1);
      const interpolatedColor = interpolateColor(startRgb, endRgb, factor);
      colors.push(`rgb(${interpolatedColor.join(",")})`);
    }

    return colors;
  };
  useEffect(() => {
    const matchedData = matchAndAnalyzeData(
      shapeData,
      data,
      selectedQuestionName
    );
    console.log(matchedData);
    setHeatmapData(matchedData);
  }, [shapeData, data, selectedQuestionName]);

  const HeatMapComponent = ({ data }) => {
    const uniqueResponses = [
      ...new Set(data.map((item) => item.mostCommonResponse)),
    ];
    const colorPalette = createGradient(
      colorStart,
      colorEnd,
      uniqueResponses.length
    );

    const responseColorMapping = uniqueResponses.reduce(
      (acc, response, index) => {
        acc[response] = colorPalette[index];
        return acc;
      },
      {}
    );

    const geoJsonStyle = (feature) => ({
      fillColor: responseColorMapping[feature.mostCommonResponse] || "#ccc",
      weight: 2,
      opacity: 1,
      color: "white", // Border color
      fillOpacity: 0.7,
    });
    const onEachFeature = (feature, layer) => {
      if (feature.properties) {
        const popupContent = `<p><strong>${feature.properties.NAME}</strong><br/>Most Common Response: ${feature.mostCommonResponse}</p>`;
        layer.bindPopup(popupContent);
      }
    };

    return (
<MapContainer
  center={[40.0583, -74.4057]} // Centered on New Jersey
  zoom={6}
  style={{ height: "500px", width: "100%" }}
  minZoom={6} maxZoom={6}
>
  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
  {data.map((item, index) => (
    <GeoJSON
      key={index}
      data={item}
      style={geoJsonStyle(item)}
      onEachFeature={onEachFeature}
    />
  ))}
</MapContainer>

    );
  };
  const [questionText, setQuestionText] = useState("");

  useEffect(() => {
    // Find the selected question based on QName
    const selectedQuestion = questions.find(q => q.QName === selectedQuestionName);

    if (!selectedQuestion) {
      setQuestionText("No question text available.");
    } else {
      // Determine text based on O_QName
      let text = (selectedQuestion.O_QName === "AGREEBAT" || selectedQuestion.O_QName === "DEIBAT") 
      ? selectedQuestion['Formal Name'] 
      : selectedQuestion.QText;

      // Split text into sentences and filter out those that are completely in uppercase
      text = text
        .split(/([.!?])\s*/)  // Splits by punctuation to maintain readability
        .filter(sentence => sentence !== sentence.toUpperCase())
        .join(' ');

      setQuestionText(text);
    }
  }, [selectedQuestionName, questions]); 

  console.log('WHAT IS THIS??', selectedQuestionName)
  
  return (
    <>
      {isLoading ? (
        <>
          <AnimatedText text={displayText} colors={colors} />
          <div
            style={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              textAlign: "center",
              color: "grey",
              fontSize: "small",
            }}
          >
            <span>
              Please allow for 10-15 seconds for the dashboard to load
            </span>
          </div>
        </>
      ) : (
        <>
          <NavBar colors={colors} Params={Params} />
          <h1
            style={{
              marginTop: "70px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {Params.association.name}
          </h1>
          <Tooltip title="Submit feedback to dashboard developer(s)">
            <button style={feedbackButtonStyle} onClick={openFeedbackModal}>
              <RateReviewIcon style={{ marginRight: "5px" }} /> Feedback
            </button>
          </Tooltip>
          <FeedbackForm
            isOpen={isFeedbackModalOpen}
            closeModal={closeFeedbackModal}
            dashboardName={Params.dashboard.name}
            colors={colors}
            Params={Params}
          />
          <SpeedDial
          className="forth-step"
            ariaLabel="Filter Speed Dial"
            onClose={() => setOpenSpeedDial(false)}
            onOpen={() => setOpenSpeedDial(true)}
            open={openSpeedDial}
            direction="up"
            style={{ position: "fixed", bottom: "20px", right: "20px" }}
            FabProps={{
              variant: "extended",
              sx: {
                bgcolor: colors.secondary,
                "&:hover": {
                  bgcolor: colors.main,
                },
              },
              onClick: () => toggleFilterMenu(),
            }}
            icon={
              <Box sx={{ display: "flex" }}>
                <Typography sx={{ color: "white" }}>FILTER</Typography>
              </Box>
            }
          >
            {Object.keys(selectedFilters).length > 0 && (
              <SpeedDialAction
                icon={<RefreshIcon />}
                tooltipTitle="Reset Filter"
                onClick={resetFilters}
              />
            )}
          </SpeedDial>

          <DynamicFilterMenu
            data={data}
            onFilterChange={applyFilters}
            isOpen={isFilterMenuOpen}
            onClose={() => setIsFilterMenuOpen(false)}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            colors={colors}
          />

      <Box
  className="first-step"
  display="flex"
  justifyContent="space-between"
  alignItems="center"
  p={2}
  gap="20px"
>
  <MiniCard
    title="Responses"
    value={
      data.length > 0
        ? new Set(data.map((data) => data.ResponseId)).size
        : "No data"
    }
  />
<MiniCard
  title="Number of Questions"
  value={
    questions.length > 0
      ? Math.max(
          ...questions.map((q) => parseInt(q.Q, 10)).filter((num) => !isNaN(num))
        )
      : "No data"
  }
/>

  <Box width="300px">
    <FormControl fullWidth>
      <InputLabel id="round-select-label">Rounds</InputLabel>
      <Select
        labelId="round-select-label"
        value={selectedRound || ""}
        onChange={(e) => setSelectedRound(e.target.value)}
      >
        {rounds.map((round) => (
          <MenuItem key={round} value={round}>
            {round}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </Box>
  <QuestionSelector
    questions={questions}
    selectedQuestionName={selectedQuestionName}
    onQuestionChange={setSelectedQuestionName}
  />
</Box>
          <Grid container justifyContent="center" alignItems="center" sx={{}}>
            <Box
              sx={{
                width: "1000px",
              }}
            >
              <p>{questionText}</p>
            </Box>
            <Grid item md={9}>
              <Typography
                sx={{pt: 2, pb: 2 }}
                color="#FFFFF"
                variant="p"
              >
                {selectedArray1[0]} and QUESTION Comparison
              </Typography>
              <Box
  sx={{
    display: "flex",
    flexDirection: "row", // Make items side by side
    height: "500px",
    textAlign: "center",
    paddingLeft: 2,
    gap: 2, // Space between charts
  }}
  className="second-step"
  data-tour="step-chart"
>
  <Box
    sx={{
      flex: 1, // Flex-grow for equal spacing
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    }}
  >
    <DynamicBarChart
      chartId={`${
        Object.keys(dynamicChartGrouping?.[0] ?? {})[0]
      }-${Object.keys(dynamicChartGrouping?.[0] ?? {})[1]}-Chart`}
      data={dynamicChartGrouping}
      variableOne={Object.keys(dynamicChartGrouping?.[0] ?? {})[1]}
      variableTwo={Object.keys(dynamicChartGrouping?.[0] ?? {})[0]}
      Params={Params}
    />
  </Box>

  {/* Conditionally render LineChartForRounds if it has data */}
  {filteredAllRoundsData?.length > 0 && selectedQuestionName && (
    <Box
      sx={{
        flex: 1, // Flex-grow for equal spacing
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
  <LineChartForRounds
    allRoundsData={filteredAllRoundsData}
    selectedQuestion={selectedQuestionName}
  />
    </Box>
  )}
</Box>

<Grid
  container
  sx={{ alignItems: "center", marginTop: 2 , marginBottom: 2}}
  data-tour="step-select"
>
  <Grid item xs={12}>
    <DropdownSelect
      selectedArray={selectedArray1}
      onSelectedArrayChange={handleSelectedArrayChange1}
    />
  </Grid>
</Grid>

              <Grid item xs={12} md={12} className="third-step">
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default DashboardComponent;
